import { combineReducers, createStore, applyMiddleware } from "redux";
import thunk from "redux-thunk";
import createSagaMiddleware from "redux-saga";
import { get } from "lodash";

import sessionReducer from "./reducers/sessionReducer";
import configurationReducer from "./reducers/configurationReducer";
import metadataReducer from "./reducers/metadataReducer";
import sideMenuReducer from "./reducers/sideMenuReducer";
import imagesReducer from "./reducers/imagesReducer";
import errorReducer from "./reducers/errorReducer";
import filterReducer from "./reducers/filterReducer";

import { SET_ERROR } from "./actions/errorActions";
import { goToModelSelector, goBackToRoom } from "../utilities/navigationUtils";
import { getAppConfig, getFrontendSettings, getLanguage, getConfigurationString } from "./sagas/selectors";
import Constants from "../constants";

/* Create root reducer, containing all features of the application */
const rootReducer = combineReducers({
    error: errorReducer,
    session: sessionReducer,
    configuration: configurationReducer,
    metadata: metadataReducer,
    images: imagesReducer,
    sideMenu: sideMenuReducer,
    filters: filterReducer,
});

/* eslint-disable @typescript-eslint/no-use-before-define */
export const initialiseSagaMiddleware = createSagaMiddleware({
    onError: e => {
        const state = store.getState();
        const frontendSettings = getFrontendSettings(state);
        const configString = getConfigurationString(state);
        const enableErrorConfirmationSetting = frontendSettings?.enable_error_confirmation;
        const hideNavigationButtonsSetting = frontendSettings?.hide_navigation_buttons;
        const searchParams = new URLSearchParams(window.location.search);
        const enableErrorConfirmationParam = searchParams.get(Constants.enableErrorConfirmationQueryParam);
        const hideNavigationButtonsParam = searchParams.get(Constants.hideNavigationButtonsQueryParam);

        const enableErrorConfirmation =
            enableErrorConfirmationParam !== null
                ? JSON.parse(enableErrorConfirmationParam)
                : enableErrorConfirmationSetting;

        const hideNavigationButtons =
            hideNavigationButtonsParam !== null ? JSON.parse(hideNavigationButtonsParam) : hideNavigationButtonsSetting;

        // eslint-disable-next-line
        console.error(e);
        if (hideNavigationButtons || enableErrorConfirmation) {
            const appConfig = getAppConfig(state);
            const language = getLanguage(state);
            store.dispatch({
                type: SET_ERROR,
                error: {
                    message: get(
                        appConfig,
                        `translations.${language}.fatalError`,
                        get(appConfig, "translations.EN.fatalError")
                    ),
                    onConfirm: hideNavigationButtons
                        ? () => goBackToRoom(configString)
                        : () =>
                              goToModelSelector(undefined, window.location.search, undefined, {
                                  replace: true,
                                  forceFullReload: true,
                              }),
                },
            });
        } else {
            hideNavigationButtons
                ? goBackToRoom(configString)
                : goToModelSelector(undefined, window.location.search, undefined, {
                      replace: true,
                      forceFullReload: true,
                  });
        }
    },
});
/* eslint-enable @typescript-eslint/no-use-before-define */

const store = createStore(rootReducer, applyMiddleware(initialiseSagaMiddleware, thunk));

export type RootState = ReturnType<typeof store.getState>;
export default store;
